import { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Button, IconButton,
  CircularProgress, Alert, Divider
} from '@mui/material';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import EnvironmentOutlined from '@ant-design/icons/EnvironmentOutlined';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

/**
 * LocationsManager — single source of truth for branches.
 *
 * Used both in /company-settings and /website-content. Both render this
 * component so edits in either place hit the same backend records.
 *
 * Props:
 *   tenantId  — company ID
 *   compact   — if true, suppresses the intro text (for embedding)
 */
export default function LocationsManager({ tenantId, compact = false }) {
  const token = localStorage.getItem('token');

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draft, setDraft] = useState({ name: '', address: '', phone: '' });

  useEffect(() => {
    if (tenantId) fetchLocations();
  }, [tenantId]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(endpoints.company.locations(tenantId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(Array.isArray(resp.data) ? resp.data : (resp.data?.results || []));
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!draft.name.trim()) {
      setError('Branch name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await axios.post(
        endpoints.company.locations(tenantId),
        {
          name: draft.name.trim(),
          address: draft.address.trim(),
          phone: draft.phone.trim(),
          display_order: locations.length
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft({ name: '', address: '', phone: '' });
      setSuccess('Branch added');
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add branch');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, patch) => {
    try {
      setError('');
      await axios.patch(
        endpoints.company.location(tenantId, id),
        patch,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update branch');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this branch? Doctor schedules using it will keep the slot but show no branch name.')) return;
    try {
      setError('');
      await axios.delete(
        endpoints.company.location(tenantId, id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove branch');
    }
  };

  return (
    <Stack spacing={2}>
      {!compact && (
        <Typography variant="body2" color="text.secondary">
          Add the branches where your clinic operates. These show up on your public website and can be assigned to doctor schedules.
        </Typography>
      )}

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {locations.length === 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No branches yet. Add one below.
            </Alert>
          )}
          {locations.map((loc) => (
            <Box key={loc.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <EnvironmentOutlined style={{ marginTop: 6, color: '#888' }} />
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    label="Branch name"
                    value={loc.name}
                    onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, name: e.target.value } : l))}
                    onBlur={(e) => handleUpdate(loc.id, { name: e.target.value })}
                  />
                  <TextField
                    size="small"
                    label="Address"
                    value={loc.address || ''}
                    multiline
                    rows={2}
                    onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, address: e.target.value } : l))}
                    onBlur={(e) => handleUpdate(loc.id, { address: e.target.value })}
                  />
                  <TextField
                    size="small"
                    label="Branch phone"
                    value={loc.phone || ''}
                    onChange={(e) => setLocations((prev) => prev.map((l) => l.id === loc.id ? { ...l, phone: e.target.value } : l))}
                    onBlur={(e) => handleUpdate(loc.id, { phone: e.target.value })}
                  />
                </Stack>
                <IconButton size="small" color="error" onClick={() => handleDelete(loc.id)}>
                  <DeleteOutlined />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Add a new branch</Typography>
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Branch name *"
            placeholder="e.g. Kaloor – Ernakulam"
            value={draft.name}
            onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            size="small"
            label="Address"
            placeholder="Street, area, city, pincode"
            multiline
            rows={2}
            value={draft.address}
            onChange={(e) => setDraft((p) => ({ ...p, address: e.target.value }))}
          />
          <TextField
            size="small"
            label="Branch phone"
            value={draft.phone}
            onChange={(e) => setDraft((p) => ({ ...p, phone: e.target.value }))}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={saving || !draft.name.trim()}
            >
              {saving ? 'Adding…' : 'Add Branch'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}
